import {CancelToken} from 'axios';
import {useTranslation} from 'react-i18next';
import {ActivityInfo} from '../../backend/interfaces/activity-info';
import {CaregiverMode} from '../../enums/caregiver-mode';
import {useInfiniteLoading} from '../../hooks/infinite-loading';
import {translations} from '../../i18n/translations';
import {ActivitiesRepository} from '../../repositories/activities';
import {ActivityCard} from '../activity-card/activity-card';
import {Box, Button} from '@mui/material';
import GenericHeader from '../generic-header/generic-header';
import {Refresh} from '@mui/icons-material';
import './activity-timeline.scss';
import {SectionList} from '../section-list/section-list';

type ActivityTimelineNewProps = {
  caregiverMode: CaregiverMode;
  participantUserId?: string;
  entityId?: string;
  headerTitleCrumbs?: {label: string; link: string}[];
  headerTitle?: string;
};

/**
 * Activity timeline component.
 *
 * Permissions are given by props.
 */
export const ActivityTimeline = ({
  caregiverMode,
  participantUserId,
  entityId,
  headerTitleCrumbs = undefined,
  headerTitle = '',
}: ActivityTimelineNewProps) => {
  // Translations
  const {t} = useTranslation();

  // Fetch activities
  const handleFetchData = async (
    limit: number,
    skip: number,
    cancelToken?: CancelToken
  ): Promise<{
    totalCount: number;
    data: Array<{
      id: string;
      item: any;
      date?: string;
    }>;
  }> => {
    try {
      // Decide by Caregiver Mode
      if (caregiverMode === CaregiverMode.SystemAdministrator) {
        // Get activities
        const results = await ActivitiesRepository.instance.getAsSystemAdministrator(
          true,
          limit,
          skip,
          entityId,
          undefined,
          participantUserId,
          cancelToken
        );

        // Return
        return {
          totalCount: results.totalCount,
          data: results.data.map((activity) => {
            return {
              id: activity.activity.id || '',
              date: activity.activity.createdAt ? (activity.activity.createdAt as any) : undefined,
              item: activity,
            };
          }),
        };
      } else if (caregiverMode === CaregiverMode.Professional) {
        // Get activities
        const results = await ActivitiesRepository.instance.getAsAffiliatedForEntity(
          entityId || '',
          true,
          limit,
          skip,
          undefined,
          participantUserId,
          cancelToken
        );

        // Return
        return {
          totalCount: results.totalCount,
          data: results.data.map((activity) => {
            return {
              id: activity.activity.id || '',
              date: activity.activity.createdAt ? (activity.activity.createdAt as any) : undefined,
              item: activity,
            };
          }),
        };
      } else if (caregiverMode === CaregiverMode.Informal) {
        // Get activities
        const results = await ActivitiesRepository.instance.getAsInformalCaregiver(
          participantUserId || '',
          true,
          limit,
          skip,
          cancelToken
        );

        // Return
        return {
          totalCount: results.totalCount,
          data: results.data.map((activity) => {
            return {
              id: activity.activity.id || '',
              date: activity.activity.createdAt ? (activity.activity.createdAt as any) : undefined,
              item: activity,
            };
          }),
        };
      } else {
        // Error
        console.debug('🔴 Could not fetch activities because caregiverMode is undefined.');

        // Return
        return {
          totalCount: 0,
          data: [],
        };
      }
    } catch (error) {
      // Error
      console.debug('🔴 Could not fetch activities.', error);

      // Return
      return {
        totalCount: 0,
        data: [],
      };
    }
  };

  // Infinite loading hook
  const {handleRefresh, isLoading, sectionListData, allLoaded} = useInfiniteLoading({
    handleFetchData,
    type: 'section-calendar',
  });

  // Render item
  const renderItem = (item: ActivityInfo) => {
    return (
      <ActivityCard
        key={`activity-card-${item.activity.id}`}
        caregiverMode={caregiverMode}
        value={item}
      />
    );
  };

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      {(headerTitleCrumbs || headerTitle) && (
        <GenericHeader
          titleCrumbs={headerTitleCrumbs && headerTitleCrumbs}
          pageTitle={headerTitle && headerTitle}
          isLoading={false}
          leftColumn={<></>}
          rightColumn={
            <Button
              className="refresh-button"
              variant="inverted"
              color="primary"
              startIcon={<Refresh />}
              onClick={handleRefresh}
            >
              {t(translations.section_list.refresh)}
            </Button>
          }
        />
      )}
      <Box
        className={`activity-timeline-container${caregiverMode === CaregiverMode.SystemAdministrator && !headerTitle ? '-sys' : ''}`}
      >
        <Box className="activity-timeline-container-centered">
          <SectionList
            data={sectionListData}
            renderItem={renderItem}
            isLoading={isLoading}
            allLoaded={allLoaded}
          />
        </Box>
      </Box>
    </Box>
  );
};
